import { v4 as uuidv4 } from "uuid";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import type { SubcategoryListingRule } from "@/lib/subcategoryRules";
import type {
  BulkListingCommonState,
  BulkListingItemDraft,
} from "@/lib/listing/bulkListingTypes";
import {
  bulkItemIsComplete,
  getBulkBatchMinimumItemError,
  getBulkItemSpecError,
} from "@/lib/listing/bulkListingValidation";
import type { CreateSellListingParams } from "@/lib/listing/createSellListing";
import { formPayloadFromBulkDraft } from "@/lib/listing/sellListingFormPayload";
import { requestBulkListingBatchSubmittedNotification } from "@/lib/listing/requestBulkListingBatchSubmittedNotification";

export type BulkSubmitResultPatch = {
  error?: string;
  succeeded?: boolean;
};

export type RunBulkSellListingSubmitArgs = {
  userId: string;
  supabase: SupabaseClient<Database>;
  bulkItems: BulkListingItemDraft[];
  bulkCommon: BulkListingCommonState;
  retryFailedOnly: boolean;
  /** Reused across retries in one modal session; null starts a new batch. */
  listingBatchId: string | null;
  activeSubcategoryIds: Set<string>;
  getRulesForSubcategory: (
    subcategoryId: string,
  ) => SubcategoryListingRule | null | undefined;
  /**
   * Must be {@link createSellListing} in production (POST /api/sell/listings).
   * Prefer {@link submitBulkSellListings} from `sellListingWorkflow` so callers
   * cannot omit the shared create entrypoint.
   */
  createListing: (
    params: CreateSellListingParams,
  ) => Promise<{ itemId: string }>;
  setBulkSubmitResult: (id: string, patch: BulkSubmitResultPatch) => void;
  onProgress: (current: number, total: number) => void;
};

export type RunBulkSellListingSubmitResult = {
  failures: number;
  targetsCount: number;
  listingBatchId: string;
  /** Created item ids (successes only), in submit order. */
  itemIds: string[];
  /** True when every target succeeded and caller should navigate to success. */
  allSucceeded: boolean;
};

/**
 * Submits bulk listing drafts through the shared create pipeline (one item at a time).
 * Per-item failures are recorded and the loop continues; batch notification fires once
 * when at least one item succeeded. Does not own UI toasts or navigation.
 */
export async function runBulkSellListingSubmit(
  args: RunBulkSellListingSubmitArgs,
): Promise<RunBulkSellListingSubmitResult> {
  const {
    userId,
    supabase,
    bulkItems,
    bulkCommon,
    retryFailedOnly,
    activeSubcategoryIds,
    getRulesForSubcategory,
    createListing,
    setBulkSubmitResult,
    onProgress,
  } = args;

  const targets = retryFailedOnly
    ? bulkItems.filter((it) => it.submitError)
    : bulkItems;

  if (targets.length === 0) {
    throw new Error("No items to submit.");
  }

  if (!retryFailedOnly) {
    const minError = getBulkBatchMinimumItemError(bulkItems);
    if (minError) {
      throw new Error(minError);
    }
  }

  const listingBatchId = args.listingBatchId ?? uuidv4();
  let failures = 0;
  let lastSuccessfulItemId: string | null = null;
  const itemIds: string[] = [];

  for (let k = 0; k < targets.length; k++) {
    const draft = targets[k];
    onProgress(k + 1, targets.length);

    if (draft.images.length !== 3 || !draft.images.some((im) => im.isMain)) {
      setBulkSubmitResult(draft.id, {
        error: "Exactly 3 images and a main image are required.",
      });
      failures++;
      continue;
    }
    if (!draft.category || !draft.subcategory) {
      setBulkSubmitResult(draft.id, { error: "Category is required." });
      failures++;
      continue;
    }
    const rule = getRulesForSubcategory(draft.subcategory);
    if (!bulkItemIsComplete(draft, rule ?? null, activeSubcategoryIds)) {
      const specError = getBulkItemSpecError(draft, activeSubcategoryIds);
      setBulkSubmitResult(draft.id, {
        error: specError ?? "Please complete all required fields and rules.",
      });
      failures++;
      continue;
    }

    try {
      const itemId = uuidv4();
      await createListing({
        supabase,
        userId,
        itemId,
        formData: formPayloadFromBulkDraft(draft, bulkCommon),
        pickupDays: bulkCommon.pickupDays,
        pickupStartHour: bulkCommon.pickupStartHour,
        pickupEndHour: bulkCommon.pickupEndHour,
        uploadedImages: draft.images,
        listingBatchId,
        listingSource: "bulk",
        locationIds: bulkCommon.locationIds ?? null,
      });
      lastSuccessfulItemId = itemId;
      itemIds.push(itemId);
      setBulkSubmitResult(draft.id, { succeeded: true });
    } catch (err) {
      failures++;
      const msg =
        err instanceof Error ? err.message : "Failed to create listing.";
      setBulkSubmitResult(draft.id, { error: msg });
    }
  }

  if (listingBatchId && lastSuccessfulItemId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token) {
        await requestBulkListingBatchSubmittedNotification(
          session.access_token,
          lastSuccessfulItemId,
        );
      }
    } catch (notifyErr) {
      console.error("bulk listing-submitted notification:", notifyErr);
    }
  }

  return {
    failures,
    targetsCount: targets.length,
    listingBatchId,
    itemIds,
    allSucceeded: failures === 0,
  };
}
